import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import Grid from '@mui/material/Grid';
import { useIntl } from 'react-intl';
import Button from '../../components/button';
import {
  HttpError,
  PraksisaktivitetAvtalerDto,
  PraksisavtaleStedBestillingspunktTypeDto,
  PraksisavtaleStedDto,
} from '../../api/generated/studiested-generated-api';
import translation from './agreements.i18n';
import * as css from './agreements.module.scss';
import { StudiestedApiContext } from '../../api/StudiestedApiContext';
import { ConfirmationDialog } from '../../components/dialog';
import { associateBy, groupBy } from '../../util';
import { NotificationFunctionContext } from '../../components/notifications/notificationProvider';
import useUnsavedChangesPrompt from '../../hooks/useUnsavedChangesPrompt';
import useScrollYOfWindow from '../../hooks/useScrollYOfWindow';
import mapNullsToUndefined from '../../util/mapNullsToUndefined';
import { mapKeysToValue } from '../../util/objectUtils';
import {
  AgreementsReducer,
  anyOmfattetAvtaleStedUndernode,
  anyOvernodeOrSiblingOrUndernodeInTarget,
  applyToAllUndernoderForStedAvtaleBestilling,
  avtaleIdFromAvtaleKey,
  collapseAllUndernoder,
  flattenAvtaler,
  flattenAvtaleSteder,
  getAvtaleKey,
  getDeaktivertAvForelderKapasitetFromAvtaler,
  getExpandedStateFromSessionStorage,
  getInitAvtaler,
  getInitExpandedAvtaleHierarki,
  getDownwardKapasitetspunkterInHierarchyFromIgnoringPathOf,
  getWindowScrollTopFromSessionStorage,
  hasBestillingOnChildren,
  mapAvtaleIdToNewAvtaleKeysForPraksisstedIder,
  mapAvtalerToNearestParent,
  praksisstedIdFromAvtaleKey,
  putExpandedStateInSessionStorage,
  putWindowScrollTopInSessionStorage,
  AgreementTableContext,
  getAvtaleIdsToPraksisstedIdsFromAvtaleKeyEntryArray,
  avtaleKeyInListOfPraksisavtaleIdToPraksisstedIderDto,
} from './methods';
import {
  AgreementsActionType,
  AgreementTableContextProps,
  ConfirmBestillingerProps,
  Direction,
  Expanded,
  FlattenedAvtaleSteder,
  HandlingsknapperProps,
  PraksisavtaleSted,
  StedAvtaleBestillingPayload,
  SubmitButton,
} from './types';
import AgreementTable from './agreementTable';
import { useActivityContextUHA } from '../activitycontexts';
import { useFormatMessageFor } from '../../i18n/translation';

const MIN_SEARCH_LENGTH = 2;
const INGEN_BESTILLING = -1;

const Agreements = () => {
  const { praksiskalenderApi } = useContext(StudiestedApiContext);
  const { praksisaktivitet, reloadPraksisaktivitet } = useActivityContextUHA();
  const { formatMessage } = useIntl();
  const { addSuccessNotification, addErrorNotification } = useContext(
    NotificationFunctionContext,
  );

  const {
    delt,
    avtaler: initAvtaler,
    deaktiverteSteder: initDeaktiverteSteder,
    skjulteSteder: initSkjulteSteder,
    kanEndre,
    praksisstederMedFordelteStudenter,
    praksisstederMedStudentOnsker,
  } = praksisaktivitet;

  const avtaler = useMemo(
    () =>
      initAvtaler?.map((avtale) => mapNullsToUndefined(avtale)) as
        | PraksisaktivitetAvtalerDto[]
        | undefined,
    [initAvtaler],
  );

  const [initAvtaleSteder, initStederDeaktivertAvForelderKapasitet] = useMemo<
    [FlattenedAvtaleSteder, string[]]
  >(
    () => [
      flattenAvtaler(avtaler),
      getDeaktivertAvForelderKapasitetFromAvtaler(avtaler),
    ],
    [avtaler],
  );

  const [state, dispatch] = useReducer(
    AgreementsReducer,
    getInitAvtaler(
      initAvtaleSteder,
      initDeaktiverteSteder,
      initSkjulteSteder,
      initStederDeaktivertAvForelderKapasitet,
    ),
  );

  const [expanded, setExpanded] = useState<Expanded>(
    getExpandedStateFromSessionStorage(praksisaktivitet.id) ||
      getInitExpandedAvtaleHierarki(avtaler),
  );

  const avtaleKeysToNearestParent = mapAvtalerToNearestParent(avtaler);

  const initBestillingModus = useMemo(
    () =>
      avtaler
        ? associateBy(
            avtaler,
            (v: PraksisaktivitetAvtalerDto) => v.avtaleId,
            (v: PraksisaktivitetAvtalerDto) => v.bestillingsmodus,
          )
        : {},
    [avtaler],
  );

  const [bestillingsmoduser, setBestillingsmoduser] = useState<{
    [avtaleId: string]: PraksisavtaleStedBestillingspunktTypeDto;
  }>(initBestillingModus);

  const [activityChanged, setActivityChanged] = useState(false);

  const addChildrenAvtalestedUtenRegistrertAdmin = (
    avtalestedUtenRegistrertAdmin: string[],
    undernoder: PraksisavtaleStedDto[],
  ) =>
    undernoder.forEach((undernode) => {
      if (
        undernode.bestilling &&
        undernode.bestilling > 0 &&
        !undernode.harRegistrertAdminBruker
      ) {
        avtalestedUtenRegistrertAdmin.push(undernode.navn);
      }

      addChildrenAvtalestedUtenRegistrertAdmin(
        avtalestedUtenRegistrertAdmin,
        undernode.undernoder,
      );
    });

  const avtalestederUtenRegistrertAdmin = () => {
    const stederUtenRegistrertAdmin: string[] = [];

    avtaler &&
      avtaler.forEach((avtale) => {
        if (
          avtale.praksissted &&
          avtale.praksissted.bestilling &&
          avtale.praksissted.bestilling > 0 &&
          !avtale.praksissted.harRegistrertAdminBruker
        ) {
          stederUtenRegistrertAdmin.push(avtale.praksissted.navn);
        }

        addChildrenAvtalestedUtenRegistrertAdmin(
          stederUtenRegistrertAdmin,
          avtale.praksissted.undernoder,
        );
      });
    return stederUtenRegistrertAdmin;
  };

  const hasBestillingOnAvtale = useCallback(
    (avtaleKey: string) =>
      Object.keys(initAvtaleSteder).some(
        (key) =>
          key === avtaleKey &&
          ((initAvtaleSteder[key]?.bestilling || INGEN_BESTILLING) > 0 ||
            hasBestillingOnChildren(initAvtaleSteder[key].undernoder)),
      ),
    [initAvtaleSteder],
  );

  const kanFlytteBestilling = useCallback(
    (avtale: PraksisaktivitetAvtalerDto) => {
      const { undernoder, id } = avtale.praksissted;
      const hasUndernoder = !!undernoder.length;

      return (
        !hasBestillingOnAvtale(getAvtaleKey(avtale.avtaleId, id)) &&
        hasUndernoder &&
        anyOmfattetAvtaleStedUndernode(undernoder)
      );
    },
    [hasBestillingOnAvtale],
  );

  const kanFlytteBestillinger = useMemo(
    () =>
      avtaler
        ?.filter((avtale) => kanFlytteBestilling(avtale))
        .map((avtale) => avtale.avtaleId) || [],
    [kanFlytteBestilling, avtaler],
  );

  const haveOvernodeOrSiblingOrUndernodeWithFordeling = useMemo(
    () =>
      Object.keys(initAvtaleSteder).filter((avtaleKey) => {
        const avtaleId = avtaleIdFromAvtaleKey(avtaleKey);
        const { undernoder, overnoderOgDeresSosken, soskennoder } =
          initAvtaleSteder[avtaleKey];

        return anyOvernodeOrSiblingOrUndernodeInTarget(
          praksisstederMedFordelteStudenter || [],
          undernoder,
          overnoderOgDeresSosken,
          soskennoder,
          avtaleId,
        );
      }),
    [initAvtaleSteder, praksisstederMedFordelteStudenter],
  );

  const haveOvernodeOrSiblingOrUndernodeWithOnske = useMemo(
    () =>
      Object.keys(initAvtaleSteder).filter((avtaleKey) => {
        const avtaleId = avtaleIdFromAvtaleKey(avtaleKey);
        const { undernoder, overnoderOgDeresSosken, soskennoder } =
          initAvtaleSteder[avtaleKey];

        const flattenedUndernoder = Object.keys(
          flattenAvtaleSteder(undernoder, avtaleId),
        ).map((undernodeAvtaleKey) =>
          praksisstedIdFromAvtaleKey(undernodeAvtaleKey),
        );

        return overnoderOgDeresSosken
          .concat(soskennoder)
          .concat(flattenedUndernoder)
          .some((praksisstedId) =>
            (praksisstederMedStudentOnsker || []).includes(praksisstedId),
          );
      }),
    [initAvtaleSteder, praksisstederMedStudentOnsker],
  );

  const kanFlytteKapasitetTil = useCallback(
    (avtaleKey: string) => {
      const avtaleId = avtaleIdFromAvtaleKey(avtaleKey);
      const { undernoder } = initAvtaleSteder[avtaleKey];

      const hasOmfattetAvtaleSted =
        bestillingsmoduser[avtaleId] ===
          PraksisavtaleStedBestillingspunktTypeDto.OmfattetAvtaleSted &&
        anyOmfattetAvtaleStedUndernode(undernoder);

      return !hasOmfattetAvtaleSted;
    },
    [initAvtaleSteder, bestillingsmoduser],
  );

  const kanFlytteKapasitetTilSteder = useMemo(
    () =>
      Object.keys(initAvtaleSteder).filter((avtaleKey) =>
        kanFlytteKapasitetTil(avtaleKey),
      ),
    [initAvtaleSteder, kanFlytteKapasitetTil],
  );

  const flyttKapasitetspunktTil = useCallback(
    (avtaleKey: string) => {
      const avtaleId = avtaleIdFromAvtaleKey(avtaleKey);
      const initSted = initAvtaleSteder[avtaleKey];
      const { undernoder, overnoder } = initSted;
      const nearestPreviousOvernodeKapasitetspunkt = overnoder.find(
        (id) => state[getAvtaleKey(avtaleId, id)].kapasitetspunkt,
      );

      let payload: StedAvtaleBestillingPayload = {
        [avtaleKey]: {
          kapasitetspunkt: true,
          deaktivertAvForelderKapasitet: false,
          kapasitet: undefined,
          kapasitetstype: undefined,
        },
      };

      const directionToMove = nearestPreviousOvernodeKapasitetspunkt
        ? Direction.DOWN
        : Direction.UP;

      if (directionToMove === Direction.UP) {
        payload = {
          ...payload,
          ...applyToAllUndernoderForStedAvtaleBestilling(avtaleId, undernoder, {
            deaktivertAvForelderKapasitet: true,
            kapasitetspunkt: false,
            kapasitetstype: undefined,
            kapasitet: undefined,
          }),
        };
      } else {
        const previousKapasitetspunktAvtaleKey = getAvtaleKey(
          avtaleId,
          nearestPreviousOvernodeKapasitetspunkt!,
        );
        const previousKapasitetspunktSted =
          initAvtaleSteder[previousKapasitetspunktAvtaleKey];

        // Inkluderer de øverste mulige nodene mellom forrige og nytt kapasitetspunkt som er utenfor den direkte stien
        const siblingsInPathOfNewKapasitetspunkt =
          getDownwardKapasitetspunkterInHierarchyFromIgnoringPathOf(
            previousKapasitetspunktSted,
            avtaleKey,
            initSted,
          );

        payload = {
          ...payload,
          ...mapKeysToValue(
            mapAvtaleIdToNewAvtaleKeysForPraksisstedIder(avtaleKey, overnoder),
            {
              deaktivertAvForelderKapasitet: false,
              kapasitetspunkt: false,
              kapasitetstype: undefined,
              kapasitet: undefined,
            },
          ),
          ...mapKeysToValue(
            mapAvtaleIdToNewAvtaleKeysForPraksisstedIder(
              avtaleKey,
              siblingsInPathOfNewKapasitetspunkt,
            ),
            {
              deaktivertAvForelderKapasitet: false,
              kapasitetspunkt: true,
              kapasitetstype: undefined,
              kapasitet: undefined,
            },
          ),
        };
      }

      dispatch({
        type: AgreementsActionType.UPDATE,
        payload,
      });
    },
    [state, initAvtaleSteder],
  );

  const [isAllowedToShare, setIsAllowedToShare] = useState<boolean>(false);

  const [missingUserNotification, setMissingUserNotification] = useState(false);

  const [avtalestederUtenAdmin, setAvtalestedUtenAdmin] = useState<string[]>(
    avtalestederUtenRegistrertAdmin(),
  );

  const setExpandedAndUpdateSessionStorage = useCallback(
    (expand: { [avtaleKey: string]: boolean }) => {
      putExpandedStateInSessionStorage(praksisaktivitet.id, expand);
      setExpanded(expand);
    },
    [praksisaktivitet.id],
  );

  const toggleExpanded = useCallback(
    (avtaleKey: string) => {
      const sted = initAvtaleSteder[avtaleKey];
      const shouldExpand = !expanded[avtaleKey];
      const avtaleId = avtaleIdFromAvtaleKey(avtaleKey);

      if (shouldExpand) {
        setExpandedAndUpdateSessionStorage({
          ...expanded,
          [avtaleKey]: shouldExpand,
        });
      } else {
        setExpandedAndUpdateSessionStorage({
          ...expanded,
          [avtaleKey]: shouldExpand,
          ...collapseAllUndernoder(avtaleId, sted.undernoder),
        });
      }
    },
    [expanded, setExpandedAndUpdateSessionStorage, initAvtaleSteder],
  );

  const addAvtalestedWithoutAdmin = useCallback(
    (praksisstedNavn: string, value: number | undefined) => {
      const isIncluded = avtalestederUtenAdmin.includes(praksisstedNavn);

      if (isIncluded && value === undefined) {
        setAvtalestedUtenAdmin(
          avtalestederUtenAdmin.filter((navn) => navn !== praksisstedNavn),
        );
      } else if (!isIncluded && value !== undefined) {
        setAvtalestedUtenAdmin([...avtalestederUtenAdmin, praksisstedNavn]);
      }
    },
    [avtalestederUtenAdmin],
  );

  const toggleBestillingstype = (
    bestillingstype: PraksisavtaleStedBestillingspunktTypeDto,
  ) => {
    switch (bestillingstype) {
      case PraksisavtaleStedBestillingspunktTypeDto.AvtaleSted:
        return PraksisavtaleStedBestillingspunktTypeDto.OmfattetAvtaleSted;
      case PraksisavtaleStedBestillingspunktTypeDto.OmfattetAvtaleSted:
        return PraksisavtaleStedBestillingspunktTypeDto.AvtaleSted;
    }
  };

  const toggleBestillingsmodus = useCallback(
    (avtaleId: string) => {
      const newBestillingsmodus = toggleBestillingstype(
        bestillingsmoduser[avtaleId],
      );
      setBestillingsmoduser({
        ...bestillingsmoduser,
        [avtaleId]: newBestillingsmodus,
      });

      const updatedExpanded = getInitExpandedAvtaleHierarki(
        avtaler?.map((avtale) =>
          avtale.avtaleId === avtaleId
            ? { ...avtale, bestillingsmodus: newBestillingsmodus }
            : avtale,
        ),
      );

      setExpandedAndUpdateSessionStorage(updatedExpanded);
    },
    [bestillingsmoduser, setExpandedAndUpdateSessionStorage, avtaler],
  );

  const onBestillingChange = useCallback(
    (avtaleId: string, praksisstedId: string, bestilling: number | undefined) =>
      dispatch({
        type: AgreementsActionType.UPDATE,
        payload: {
          [getAvtaleKey(avtaleId, praksisstedId)]: {
            bestilling,
          },
        },
      }),
    [],
  );

  const onKapasitetChange = useCallback(
    (avtaleId: string, praksisstedId: string, kapasitet: number | undefined) =>
      dispatch({
        type: AgreementsActionType.UPDATE,
        payload: {
          [getAvtaleKey(avtaleId, praksisstedId)]: {
            kapasitet,
          },
        },
      }),
    [],
  );

  const handleDeaktivertPraksissted = useCallback(
    (key: string) => {
      const avtaleId = avtaleIdFromAvtaleKey(key);
      const undernoder = flattenAvtaleSteder(
        initAvtaleSteder[key].undernoder,
        avtaleId,
      );

      const deaktiverteSteder = Object.entries(state)
        .filter(([_, value]) => value.deaktivert)
        .map(([avtaleKey]) => avtaleKey);

      const deaktivert = !deaktiverteSteder.includes(key);

      const formattedUndernoder: StedAvtaleBestillingPayload = Object.keys(
        undernoder,
      ).reduce(
        (acc: StedAvtaleBestillingPayload, undernodeKey: string) => ({
          ...acc,
          [undernodeKey]: {
            deaktivert,
            hasDeaktivertForelder: deaktivert,
          },
        }),
        {},
      );

      dispatch({
        type: AgreementsActionType.UPDATE,
        payload: {
          ...formattedUndernoder,
          [key]: { deaktivert },
        },
      });
    },
    [initAvtaleSteder, state],
  );

  const handleSkjultPraksissted = useCallback(
    (key: string) => {
      const avtaleId = avtaleIdFromAvtaleKey(key);
      const undernoder = flattenAvtaleSteder(
        initAvtaleSteder[key].undernoder,
        avtaleId,
      );

      const skjulteSteder = Object.entries(state)
        .filter(([_, value]) => value.skjult)
        .map(([avtaleKey]) => avtaleKey);

      const skjult = !skjulteSteder.includes(key);

      const formattedUndernoder: StedAvtaleBestillingPayload = Object.keys(
        undernoder,
      ).reduce(
        (acc: StedAvtaleBestillingPayload, undernodeKey: string) => ({
          ...acc,
          [undernodeKey]: {
            skjult,
            hasSkjultForelder: skjult,
          },
        }),
        {},
      );

      dispatch({
        type: AgreementsActionType.UPDATE,
        payload: {
          ...formattedUndernoder,
          [key]: { skjult },
        },
      });
    },
    [initAvtaleSteder, state],
  );

  const kapasitetHasChanged = useMemo<boolean>(
    () =>
      Object.keys(state).some(
        (avtaleKey) =>
          state[avtaleKey]?.kapasitet !==
          initAvtaleSteder[avtaleKey]?.kapasitet,
      ),
    [state, initAvtaleSteder],
  );

  const kapasitetspunktHasChanged = useMemo<boolean>(
    () =>
      Object.keys(state).some(
        (avtaleKey) =>
          state[avtaleKey]?.kapasitetspunkt !==
          initAvtaleSteder[avtaleKey]?.kapasitetspunkt,
      ),
    [state, initAvtaleSteder],
  );

  const bestillingHasChanged = useMemo<boolean>(
    () =>
      Object.keys(state).some(
        (avtaleKey) =>
          state[avtaleKey]?.bestilling !==
          initAvtaleSteder[avtaleKey]?.bestilling,
      ),
    [state, initAvtaleSteder],
  );

  const deaktiverteStederHasChanged = useMemo<boolean>(
    () =>
      Object.keys(state).some(
        (avtaleKey) =>
          state[avtaleKey].deaktivert !==
          avtaleKeyInListOfPraksisavtaleIdToPraksisstedIderDto(
            avtaleKey,
            initDeaktiverteSteder,
          ),
      ),
    [initDeaktiverteSteder, state],
  );

  const skjulteStederHasChanged = useMemo<boolean>(
    () =>
      Object.keys(state).some(
        (avtaleKey) =>
          state[avtaleKey].skjult !==
          avtaleKeyInListOfPraksisavtaleIdToPraksisstedIderDto(
            avtaleKey,
            initSkjulteSteder,
          ),
      ),
    [initSkjulteSteder, state],
  );

  const bestillingsmodusHasChanged = useMemo<boolean>(
    () =>
      Object.keys(bestillingsmoduser).some(
        (key) => bestillingsmoduser[key] !== initBestillingModus[key],
      ),
    [bestillingsmoduser, initBestillingModus],
  );

  useEffect(() => {
    setActivityChanged(
      kapasitetHasChanged ||
        kapasitetspunktHasChanged ||
        bestillingHasChanged ||
        deaktiverteStederHasChanged ||
        skjulteStederHasChanged ||
        bestillingsmodusHasChanged,
    );
  }, [
    kapasitetHasChanged,
    kapasitetspunktHasChanged,
    bestillingHasChanged,
    deaktiverteStederHasChanged,
    skjulteStederHasChanged,
    bestillingsmodusHasChanged,
  ]);

  useEffect(() => {
    if (!delt) {
      setIsAllowedToShare(
        Object.values(initAvtaleSteder).some(
          (sted) => sted.bestilling && sted.bestilling > 0,
        ) ||
          Object.values(state).some(
            (sted) => sted.bestilling && sted.bestilling > 0,
          ),
      );
    }
  }, [delt, initAvtaleSteder, state]);

  const saveHandler = (buttonClicked: SubmitButton): void => {
    if (
      ((buttonClicked === SubmitButton.Share && !delt) ||
        (buttonClicked === SubmitButton.Save && delt)) &&
      avtalestederUtenAdmin.length > 0
    ) {
      setMissingUserNotification(true);
    } else {
      sendTilBackend(buttonClicked);
    }
  };

  const missingUserConfirmationClose = (confirm: boolean) => {
    setMissingUserNotification(false);

    if (confirm) {
      sendTilBackend(SubmitButton.Share);
    }
  };

  const sendTilBackend = (buttonClicked: SubmitButton) => {
    const steder = Object.entries(state);

    const bestillinger = steder
      .filter(([_, sted]) => sted.bestilling !== undefined)
      .map(([avtaleKey, sted]) => ({
        avtaleId: avtaleIdFromAvtaleKey(avtaleKey),
        bestilling: sted.bestilling!,
        bestillingsmodus: bestillingsmoduser[avtaleIdFromAvtaleKey(avtaleKey)],
        praksisstedId: praksisstedIdFromAvtaleKey(avtaleKey),
      }));

    const kapasiteter = steder
      .filter(([avtaleKey, sted]) => {
        const initAvtaleSted = initAvtaleSteder[avtaleKey];

        return (
          // Only submit capacity point if there been a change
          // Only submit capacity if sted is leaf node or capacity point
          !(
            sted.kapasitet === undefined && sted.kapasitetspunkt === undefined
          ) &&
          (initAvtaleSted.undernoder.length === 0 ||
            sted.kapasitetspunkt === true) && // Only submit capacity if there's been a change
          (initAvtaleSted.kapasitet !== sted.kapasitet ||
            initAvtaleSted.kapasitetspunkt !== sted.kapasitetspunkt)
        );
      })
      .map(([avtaleKey, sted]) => ({
        avtaleId: avtaleIdFromAvtaleKey(avtaleKey),
        praksisstedId: praksisstedIdFromAvtaleKey(avtaleKey),
        kapasitet: sted.kapasitet,
        kapasitetspunkt: sted.kapasitetspunkt,
        bestillingsmodus: bestillingsmoduser[avtaleIdFromAvtaleKey(avtaleKey)],
      }));

    const deaktiverteSteder = steder.filter(
      ([avtaleKey]) => state[avtaleKey].deaktivert,
    );

    const deaktiverteStederByAvtaleIds =
      getAvtaleIdsToPraksisstedIdsFromAvtaleKeyEntryArray(
        groupBy(deaktiverteSteder, ([avtaleKey]) =>
          avtaleIdFromAvtaleKey(avtaleKey),
        ),
      );

    const skjulteSteder = steder.filter(
      ([avtaleKey]) => state[avtaleKey].skjult,
    );

    const skjulteStederByAvtaleIds =
      getAvtaleIdsToPraksisstedIdsFromAvtaleKeyEntryArray(
        groupBy(skjulteSteder, ([avtaleKey]) =>
          avtaleIdFromAvtaleKey(avtaleKey),
        ),
      );

    praksiskalenderApi
      .praksisaktivitetIdBestillingerPut({
        pathParams: { id: praksisaktivitet.id },
        praksisaktivitetBestillingPutDto: {
          delt: delt || buttonClicked === SubmitButton.Share,
          bestillinger,
          kapasiteter,
          deaktiverteSteder: deaktiverteStederByAvtaleIds,
          skjulteSteder: skjulteStederByAvtaleIds,
          // TODO kapasitetspunkt (mulig deaktivertAvForelderkapasitet etc.)
        },
      })
      .then(() => {
        addSuccessNotification(
          formatMessage({ id: translation.saveSuccess.id }),
        );
        reloadPraksisaktivitet();
      })
      .catch((err: HttpError) => addErrorNotification(err.body));
  };

  const allParentNodesAreExpanded = useCallback(
    (avtaleKey: string): boolean => {
      const parentKey = avtaleKeysToNearestParent[avtaleKey];
      const expandedParentKey = expanded[parentKey];

      if (avtaleKey === parentKey && expandedParentKey) return true;

      return expandedParentKey && allParentNodesAreExpanded(parentKey);
    },
    [avtaleKeysToNearestParent, expanded],
  );

  const isExpanded = useCallback(
    (agreementRow: PraksisavtaleSted) => {
      const avtaleKey = getAvtaleKey(agreementRow.avtaleId, agreementRow.id);
      return expanded[avtaleKey] || allParentNodesAreExpanded(avtaleKey);
    },
    [expanded, allParentNodesAreExpanded],
  );

  const shouldDisplayPraksissted = useCallback(
    (agreementrow: PraksisavtaleSted, tableSearch: string) => {
      if (tableSearch.length > MIN_SEARCH_LENGTH) {
        return (
          agreementrow.navn
            .toLowerCase()
            .indexOf(tableSearch.toLocaleLowerCase()) > -1
        );
      }

      return agreementrow.topNode || isExpanded(agreementrow);
    },
    [isExpanded],
  );

  const windowScroll = useScrollYOfWindow();

  useEffect(() => {
    if (windowScroll)
      putWindowScrollTopInSessionStorage(praksisaktivitet.id, windowScroll);
  }, [windowScroll, praksisaktivitet.id]);

  useEffect(
    () =>
      window.scroll({
        top: getWindowScrollTopFromSessionStorage(praksisaktivitet.id),
      }),
    [praksisaktivitet.id],
  );

  const agreementTableContext: AgreementTableContextProps = useMemo(
    () => ({
      praksisaktivitetId: praksisaktivitet.id,
      state,
      avtaler,
      shouldDisplayPraksissted,
      expanded,
      toggleExpanded,
      onBestillingChange,
      onKapasitetChange,
      kanEndre,
      delt,
      handleDeaktivertPraksissted,
      handleSkjultPraksissted,
      bestillingsmoduser,
      toggleBestillingsmodus,
      addAvtalestedWithoutAdmin,
      kanFlytteBestillinger,
      kanFlytteKapasitetTilSteder,
      flyttKapasitetspunktTil,
      haveOvernodeOrSiblingOrUndernodeWithFordeling,
      haveOvernodeOrSiblingOrUndernodeWithOnske,
    }),
    [
      state,
      avtaler,
      shouldDisplayPraksissted,
      expanded,
      toggleExpanded,
      onBestillingChange,
      onKapasitetChange,
      kanEndre,
      delt,
      handleDeaktivertPraksissted,
      handleSkjultPraksissted,
      bestillingsmoduser,
      toggleBestillingsmodus,
      addAvtalestedWithoutAdmin,
      kanFlytteBestillinger,
      kanFlytteKapasitetTilSteder,
      praksisaktivitet,
      flyttKapasitetspunktTil,
      haveOvernodeOrSiblingOrUndernodeWithFordeling,
      haveOvernodeOrSiblingOrUndernodeWithOnske,
    ],
  );

  return (
    <div className={css.agreements}>
      <Grid container>
        <Grid item xs={12}>
          <AgreementTableContext.Provider value={agreementTableContext}>
            <AgreementTable />
          </AgreementTableContext.Provider>
        </Grid>
        <Handlingsknapper
          delt={delt}
          kanEndre={kanEndre}
          activityChanged={activityChanged}
          isAllowedToShare={isAllowedToShare}
          onSave={() => {
            saveHandler(SubmitButton.Save);
          }}
          onShare={() => {
            saveHandler(SubmitButton.Share);
          }}
        />
      </Grid>
      <ConfirmBestillinger
        onClose={missingUserConfirmationClose}
        isOpen={missingUserNotification}
        avtalestederUtenAdmin={avtalestederUtenAdmin}
      />
    </div>
  );
};

// Exported for tests
export const MAX_NUM_STEDER = 8;
export const ConfirmBestillinger: React.FC<
  React.PropsWithChildren<ConfirmBestillingerProps>
> = ({ onClose, isOpen, avtalestederUtenAdmin }) => {
  const stederWithConstraint = avtalestederUtenAdmin.slice(0, MAX_NUM_STEDER);
  const stederRemaining = avtalestederUtenAdmin.slice(MAX_NUM_STEDER);

  const [showRest, setShowRest] = useState<boolean>(false);

  const {
    missingPraksisstedbrukerTitle: title,
    missingPraksisstedbrukerSubmit: submitLabel,
    showRemainingPlaces: showRemaining,
    hideRemainingPlaces: hideRemaining,
    missingPraksisstedbruker,
  } = useFormatMessageFor({
    ...translation,
    showRemainingPlaces: {
      ...translation.showRemainingPlaces,
      values: { numPlaces: stederRemaining.length.toString() },
    },
    hideRemainingPlaces: {
      ...translation.hideRemainingPlaces,
      values: { numPlaces: stederRemaining.length.toString() },
    },
  });

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      submitLabel={submitLabel}
    >
      <p>{missingPraksisstedbruker}</p>
      <ul className={css.confirmationDialogList}>
        {(showRest ? avtalestederUtenAdmin : stederWithConstraint).map(
          (navn) => (
            <li key={navn}> - {navn}</li>
          ),
        )}
      </ul>
      {stederRemaining.length > 0 && (
        <Button.Quaternary
          onClick={() => setShowRest(!showRest)}
          testSelector={'confirm-show-hide-places-btn'}
        >
          {showRest ? hideRemaining : showRemaining}
        </Button.Quaternary>
      )}
    </ConfirmationDialog>
  );
};

const Handlingsknapper: React.FC<
  React.PropsWithChildren<HandlingsknapperProps>
> = ({
  delt,
  kanEndre,
  activityChanged,
  isAllowedToShare,
  onSave,
  onShare,
}) => {
  const labels = useFormatMessageFor(translation);

  useUnsavedChangesPrompt(activityChanged);

  return (
    <Grid item xs={12}>
      <div className={css.buttonContainer}>
        {kanEndre && !delt && (
          <Button.Secondary disabled={!isAllowedToShare} onClick={onShare}>
            {labels.share}
          </Button.Secondary>
        )}
        {kanEndre && (
          <Button.Secondary
            disabled={!activityChanged}
            onClick={onSave}
            className="saveButton"
            testSelector="agreementSaveButton"
          >
            {delt ? labels.saveAndShare : labels.save}
          </Button.Secondary>
        )}
      </div>
    </Grid>
  );
};

export default Agreements;
